import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { WifiOff, BookOpen, Target, ArrowRight, CheckCircle2 } from "lucide-react";

const UNIVERS_IMG = "https://static.prod-images.emergentagent.com/jobs/1304f033-c917-4974-91b0-9d4153e7c191/images/fc332f8d14693b8ad1bc674e6f585bb5d828bdc86b4eddc8da2de345129dde94.jpeg";

const REFERENTIEL = [
  { year: "3e primaire", theme: "La Découverte", skills: ["Trier des informations", "Découvrir le matériel", "Premiers pas sécurité"] },
  { year: "4e primaire", theme: "Je deviens acteur", skills: ["Construire un algorithme", "Créer un contenu", "Publier de façon responsable"] },
  { year: "5e primaire", theme: "L'analyse critique", skills: ["Vérifier une information", "Citer ses sources", "Programmer un parcours"] },
  { year: "6e primaire", theme: "Le citoyen numérique", skills: ["Communiquer avec respect", "Protéger sa vie privée", "Adopter une charte"] },
];

export default function Univers() {
  return (
    <div>
      <section className="bg-gradient-to-b from-sky-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Notre univers</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2">
              Une pédagogie qui a du sens
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Chez Kwarizmix, nous croyons que la culture numérique se construit
              par l'expérience. Nos ateliers alternent manipulation concrète, jeu
              collectif et réflexion, pour que chaque enfant devienne acteur du numérique.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-sky-500/20 rotate-2">
            <img src={UNIVERS_IMG} alt="Atelier Kwarizmix" className="w-full h-[380px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Plan B */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-slate-900 rounded-[2.5rem] p-10 sm:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-300 text-sm font-bold">
              <WifiOff className="w-4 h-4" /> Méthodologie hors ligne
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white mt-4">
              Le "Plan B" : apprendre même sans écran
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Une panne technique ne doit jamais gâcher un atelier. Chaque activité
              dispose d'une version 100% débranchée : jeux de rôle, cartes, gommettes
              et manipulation papier. Les concepts informatiques restent les mêmes,
              l'apprentissage continue.
            </p>
          </div>
          <div className="grid gap-4">
            {["Jeux de rôle et mises en situation", "Cartes d'instructions et algorithmes papier", "Badges et gommettes de progression", "Aucune dépendance à Internet"].map((t) => (
              <div key={t} className="flex items-center gap-3 bg-slate-800 rounded-2xl p-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
                <span className="text-slate-200 font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referentiel */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-2xl mb-10">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-500">
            <BookOpen className="w-4 h-4" /> Le référentiel FMTTN
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">
            Aligné sur le volet numérique officiel
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Nos ateliers suivent la progression des compétences numériques du
            référentiel, année par année.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {REFERENTIEL.map((r, i) => (
            <motion.div
              key={r.year}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]"
            >
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sky-100 text-sky-600 mb-4">
                <Target className="w-6 h-6" />
              </span>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{r.year}</p>
              <h3 className="font-heading font-extrabold text-lg text-slate-900 mt-1 mb-4">{r.theme}</h3>
              <ul className="space-y-2">
                {r.skills.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/nos-activites" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-105 transition-[transform,background-color]">
            Voir les ateliers correspondants <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
